import { Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

export interface UpgradeSubscriptionDialogData {
  /** Wymagany minimalny stage (1=start, 2=pro, 3=business) */
  requiredStage: 1 | 2 | 3;
  /** Nazwa funkcji która wymaga wyższego planu */
  featureName: string;
  /** Ikona funkcji (opcjonalna, domyślnie na podstawie requiredStage) */
  featureIcon?: string;
  /** Opis funkcji (opcjonalny) */
  featureDescription?: string;
}

@Component({
  selector: 'app-upgrade-subscription-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './upgrade-subscription-dialog.component.html',
  styleUrls: ['./upgrade-subscription-dialog.component.scss']
})
export class UpgradeSubscriptionDialogComponent {
  private readonly _router = inject(Router);
  
  constructor(
    public dialogRef: MatDialogRef<UpgradeSubscriptionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UpgradeSubscriptionDialogData
  ) {}

  get planLabel(): string {
    const labels: Record<number, string> = {
      1: 'START',
      2: 'PRO',
      3: 'BUSINESS'
    };
    return labels[this.data.requiredStage] || 'START';
  }

  get planIcon(): string {
    if (this.data.featureIcon) return this.data.featureIcon;
    
    const icons: Record<number, string> = {
      1: 'tabler_outline:rocket',
      2: 'tabler_outline:crown',
      3: 'tabler_outline:building-skyscraper'
    };
    return icons[this.data.requiredStage] || 'tabler_outline:star';
  }

  get planGradient(): string {
    const gradients: Record<number, string> = {
      1: 'from-primary-500 via-primary-600 to-blue-600',
      2: 'from-amber-400 via-amber-500 to-orange-500',
      3: 'from-purple-500 via-purple-600 to-violet-600'
    };
    return gradients[this.data.requiredStage] || 'from-primary-500 to-primary-600';
  }

  get planBgGradient(): string {
    const gradients: Record<number, string> = {
      1: 'from-primary-500/20 to-blue-500/20',
      2: 'from-amber-500/20 to-orange-500/20',
      3: 'from-purple-500/20 to-violet-500/20'
    };
    return gradients[this.data.requiredStage] || 'from-primary-500/20 to-primary-600/20';
  }

  get buttonGradient(): string {
    const gradients: Record<number, string> = {
      1: 'from-primary-500 via-primary-600 to-blue-600 hover:from-primary-400 hover:via-primary-500 hover:to-blue-500',
      2: 'from-amber-400 via-amber-500 to-orange-500 hover:from-amber-300 hover:via-amber-400 hover:to-orange-400',
      3: 'from-purple-500 via-purple-600 to-violet-600 hover:from-purple-400 hover:via-purple-500 hover:to-violet-500'
    };
    return gradients[this.data.requiredStage] || 'from-primary-500 to-primary-600';
  }

  get buttonTextColor(): string {
    return this.data.requiredStage === 2 ? 'text-gray-900' : 'text-white';
  }

  get shadowColor(): string {
    const shadows: Record<number, string> = {
      1: 'shadow-primary-500/25 hover:shadow-primary-500/40',
      2: 'shadow-amber-500/25 hover:shadow-amber-500/40',
      3: 'shadow-purple-500/25 hover:shadow-purple-500/40'
    };
    return shadows[this.data.requiredStage] || 'shadow-primary-500/25';
  }

  get badgeTextColor(): string {
    const colors: Record<number, string> = {
      1: 'text-primary-600 dark:text-primary-400',
      2: 'text-amber-600 dark:text-amber-400',
      3: 'text-purple-600 dark:text-purple-400'
    };
    return colors[this.data.requiredStage] || 'text-primary-600 dark:text-primary-400';
  }

  goToSubscription(): void {
    this.dialogRef.close();
    this._router.navigate(['/konto/subskrypcje'], {
      queryParams: { stage: this.data.requiredStage }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
